<script setup lang="ts">
    import { ChevronDown, Check, Search, CircleMinus, LoaderCircle } from 'lucide-vue-next';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxItemIndicator,
        ComboboxVirtualizer,
        ComboboxEmpty,
        ComboboxRoot,
        ComboboxTrigger,
        ComboboxViewport,
        ComboboxLabel,
        ComboboxSeparator,
        useFilter,
        AcceptableValue,
    } from 'reka-ui';
    import { computed, ref } from 'vue';
    import { SelectOption } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { Button } from '@/components/ui/button';
    import { cn } from '@/lib/utils';

    interface Props {
        options: SelectOption[] | Record<string, any>[];
        placeholder?: string;
        translate?: boolean;
        loading?: boolean;
        showAddon?: boolean;
        labelBy?: string;
        valueBy?: string;
        showRemoveButton?: boolean;
        contentClass?: string;
        position?: 'popper' | 'inline';
        side?: 'top' | 'bottom';
        disabled?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        translate: false,
        loading: false,
        showAddon: true,
        labelBy: 'label',
        valueBy: 'value',
        showRemoveButton: true,
        position: 'popper',
        side: 'bottom',
    });

    const model = defineModel<AcceptableValue>();

    const { t } = useI18n();

    const searchTerm = ref('');
    const { contains } = useFilter({ sensitivity: 'base' });

    const getOptionValue = (option: SelectOption | Record<string, any>): string => {
        return (option as Record<string, any>)[props.valueBy];
    };

    const getOptionLabel = (option: SelectOption | Record<string, any>): string => {
        const label = (option as Record<string, any>)[props.labelBy];
        return props.translate ? t(label) : label;
    };

    const translatedOptions = computed(() =>
        props.options.map((option) => ({
            ...option,
            value: getOptionValue(option),
            label: getOptionLabel(option),
            addon: option.addon,
            is_group: option.is_group,
            is_seperator: option.is_seperator,
            disabled: option.disabled,
        })),
    );

    const filteredOptions = computed(() =>
        translatedOptions.value.filter(
            (option) =>
                option.is_group ||
                option.is_seperator ||
                contains(option.label, searchTerm.value) ||
                option.value === null,
        ),
    );

    const placeholderWithDefault = computed(
        () => props.placeholder || t('forms.select_placeholder'),
    );

    const selectedOption = computed(() => {
        return translatedOptions.value.find((opt) => opt.value === model.value);
    });

    const open = ref(false);
</script>

<template>
    <ComboboxRoot
        v-model="model"
        v-model:open="open"
        :multiple="false"
        class="group relative"
        :disabled="disabled"
    >
        <ComboboxAnchor class="group-data-[disabled]:bg-faded flex w-full gap-2">
            <ComboboxTrigger as-child>
                <Button
                    variant="input"
                    :class="!selectedOption && 'text-faded-foreground'"
                    class="flex-1 transition-opacity duration-0 disabled:opacity-100"
                    size="xs"
                    :disabled="loading || disabled"
                >
                    {{ selectedOption ? selectedOption.label : placeholderWithDefault }}
                    <ChevronDown
                        v-if="!loading"
                        class="text-foreground/50 size-4 cursor-pointer"
                    />
                    <LoaderCircle
                        v-else
                        class="text-foreground/50 ml-auto size-4 animate-spin"
                    />
                </Button>
            </ComboboxTrigger>
            <Button
                v-if="model && showRemoveButton && !disabled"
                variant="ghost_destructive"
                size="xs"
                width="narrow"
                class="text-muted-foreground"
                @click.prevent="model = null"
            >
                <CircleMinus />
            </Button>
        </ComboboxAnchor>

        <ComboboxContent
            :class="
                cn(
                    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 absolute z-50 mt-1 max-h-[200px] origin-top overflow-y-auto rounded-md border bg-white shadow-md will-change-[opacity,transform]',
                    position === 'inline'
                        ? 'w-full'
                        : 'origin-[50% 0px] w-[var(--reka-popper-anchor-width)]',
                    side === 'top' && 'translate-y-[calc(-100%-10px)]',
                    contentClass,
                )
            "
            @mousedown.stop="(event: any) => event.preventDefault()"
            :position="position"
            side-flip
            align="start"
            :side="side"
        >
            <div v-if="!!options.length">
                <div class="sticky top-0 z-10 w-full items-center bg-white">
                    <ComboboxInput
                        v-model="searchTerm"
                        :display-value="() => ''"
                        class="h-7.5 w-full rounded-none border-0 border-b pl-7.5 text-xs outline-none focus-visible:ring-0"
                        :placeholder="t('forms.search')"
                    />
                    <span
                        class="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center px-2"
                    >
                        <Search class="text-faded-foreground size-3.5" />
                    </span>
                </div>
                <ComboboxViewport class="p-1">
                    <ComboboxVirtualizer
                        v-slot="{ option }"
                        :options="filteredOptions"
                        :text-content="(opt: SelectOption) => opt.label"
                        :estimate-size="24"
                    >
                        <ComboboxLabel
                            v-if="option.is_group"
                            :key="option.label"
                            class="text-foreground/50 mx-2 w-full px-0 py-1.5 text-xs leading-none"
                        >
                            {{ option.label }}
                        </ComboboxLabel>
                        <div
                            v-else-if="option.is_seperator"
                            class="flex h-6 w-full items-center px-2"
                        >
                            <ComboboxSeparator
                                class="bg-border h-[1px] w-full"
                                :key="`${option.value}`"
                            />
                        </div>
                        <ComboboxItem
                            v-else
                            class="data-[state=checked]:text-primary text-foreground data-[disabled]:text-muted-foreground data-[highlighted]:bg-primary/10 relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-2 text-xs leading-none select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[state=checked]:pr-8"
                            :value="option.value"
                            :disabled="option.disabled"
                        >
                            <span class="truncate">
                                {{ option.label }}
                            </span>
                            <span
                                v-if="showAddon && option.addon"
                                class="text-muted-foreground ml-auto text-[10px]"
                            >
                                {{ option.addon }}
                            </span>
                            <span
                                class="absolute right-2 flex size-3.5 items-center justify-center"
                            >
                                <ComboboxItemIndicator>
                                    <Check class="text-primary size-3" />
                                </ComboboxItemIndicator>
                            </span>
                        </ComboboxItem>
                    </ComboboxVirtualizer>
                    <ComboboxEmpty>
                        <span class="text-muted-foreground p-2 text-xs">{{
                            t('forms.found_no_options')
                        }}</span>
                    </ComboboxEmpty>
                </ComboboxViewport>
            </div>
            <ComboboxViewport
                v-else
                class="p-1"
            >
                <span class="text-muted-foreground p-2 text-xs">{{
                    loading ? t('forms.loading') : t('forms.no_options')
                }}</span>
            </ComboboxViewport>
        </ComboboxContent>
    </ComboboxRoot>
</template>
